import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import HostedPage from './index'
import usePaymentSessionTimeoutFromAPI from '../../hooks/usePaymentSessionTimeoutFromAPI'
import useIdleTimeout from '../../hooks/useIdleTimeout'
import useDynamicSessionTimeout from '../../hooks/useDynamicSessionTimeout'
// import IdleTimeoutModal from '../idle-timeout-modal' // Commented out - modal hidden
import SessionTimeoutScreen from '../session-timeout-screen'
import TimeoutDebugPanel from '../timeout-debug-panel'
import DebugCogToggle from '../debug-cog-toggle'
import TimeoutCountdownBar from '../timeout-countdown-bar'
import { getSessionTimeoutConfig, validateTimeoutConfig, logTimeoutEvent } from '../../utils/session-timeout-config'
import messageService from '../../utils/message-service'

/**
 * Wrapper component that adds idle timeout functionality to the SDK page
 */
const SdkPageWithTimeout = props => {
    const { id: paymentSessionId } = useParams()
    const [timeoutConfig, setTimeoutConfig] = useState(null)
    const [isConfigLoaded, setIsConfigLoaded] = useState(false)
    const [showTimeoutScreen, setShowTimeoutScreen] = useState(false)
    const [showDebugPanel, setShowDebugPanel] = useState(false)
    const [windowOpened, setWindowOpened] = useState(false)
    const [dynamicTimeoutEnabled, setDynamicTimeoutEnabled] = useState(false)
    const [hideCountdownBar, setHideCountdownBar] = useState(false)

    // Load timeout configuration on mount
    useEffect(() => {
        const loadConfig = async () => {
            try {
                const config = await getSessionTimeoutConfig()
                const validatedConfig = validateTimeoutConfig(config)

                // eslint-disable-next-line no-console
                console.log('[SdkPageWithTimeout] Loaded config:', validatedConfig)
                // eslint-disable-next-line no-console
                console.log('[SdkPageWithTimeout] Dynamic timeout config:', validatedConfig.dynamicTimeout)
                // eslint-disable-next-line no-console
                console.log('[SdkPageWithTimeout] Dynamic timeout enabled:', validatedConfig.dynamicTimeout?.enabled)

                setTimeoutConfig(validatedConfig)
                setIsConfigLoaded(true)
                setWindowOpened(window.opener != null)

                // Check if dynamic timeout is enabled in config
                const isDynamicEnabled = validatedConfig.dynamicTimeout?.enabled || false
                // eslint-disable-next-line no-console
                console.log('[SdkPageWithTimeout] Setting dynamicTimeoutEnabled to:', isDynamicEnabled)
                setDynamicTimeoutEnabled(isDynamicEnabled)
            } catch (error) {
                console.error('Failed to load timeout configuration:', error)
                setIsConfigLoaded(true) // Still set to true to prevent infinite loading
            }
        }

        loadConfig()
    }, [])

    // Handle timeout events
    const handleTimeout = () => {
        logTimeoutEvent('timeout', {
            sessionDuration: Date.now() - sessionStartTime,
        })

        // Close any open payment modals
        if (window.DataMesh && typeof window.DataMesh.destroy === 'function') {
            try {
                window.DataMesh.destroy()
            } catch (e) {
                console.error('Error destroying DataMesh SDK on timeout:', e)
            }
        }

        // In test environment, redirect immediately
        if (process.env.NODE_ENV === 'test') {
            const redirectUrl = timeoutConfig?.redirectUrl || timeoutConfig?.logoutUrl || window.location.origin
            window.location.href = redirectUrl
            return
        }

        // Show timeout screen instead of redirecting immediately
        setShowTimeoutScreen(true)
    }

    const handleWarning = () => {
        logTimeoutEvent('warning', {
            remainingTime: remainingTime,
        })

        // Send timeout warning message using the message service
        messageService.sendTimeoutWarning(remainingTime, {
            sessionDuration: Date.now() - sessionStartTime,
        })
    }

    const handleActivity = () => {
        // Optional: Log user activity for analytics
        logTimeoutEvent('activity', { timestamp: Date.now() })
    }

    const handleExtendSession = () => {
        logTimeoutEvent('extended', {
            extendedAt: Date.now(),
        })
        extendSession()
    }

    // handleLogout commented out - no longer used since IdleTimeoutModal is hidden
    // const handleLogout = () => {
    //     logTimeoutEvent('manual_logout', {
    //         loggedOutAt: Date.now(),
    //     })

    //     // Close any open payment modals
    //     if (window.DataMesh && typeof window.DataMesh.destroy === 'function') {
    //         try {
    //             window.DataMesh.destroy()
    //         } catch (e) {
    //             console.error('Error destroying DataMesh SDK on logout:', e)
    //         }
    //     }

    //     // In test environment, redirect immediately
    //     if (process.env.NODE_ENV === 'test') {
    //         const redirectUrl = timeoutConfig?.redirectUrl || timeoutConfig?.logoutUrl || window.location.origin
    //         window.location.href = redirectUrl
    //         return
    //     }

    //     // Show timeout screen for manual logout too
    //     setShowTimeoutScreen(true)
    // }

    // Handle closing the timeout screen (window close)
    const handleTimeoutScreenClose = () => {
        // Send final message using the message service
        messageService.sendWindowClosing('Session timeout - window closing', {
            sessionDuration: Date.now() - sessionStartTime,
        })

        // Close the window or redirect
        if (windowOpened) {
            // If opened as popup, close the window
            window.close()
        } else {
            // If in iframe or direct navigation, redirect
            const redirectUrl = timeoutConfig?.redirectUrl || timeoutConfig?.logoutUrl || window.location.origin
            window.location.href = redirectUrl
        }
    }

    // Track session start time
    const [sessionStartTime] = useState(Date.now())

    // For SDK page, always use config-based timeout to support dynamic timeout changes
    // API-based timeout doesn't support changing timeout dynamically
    const apiTimeoutHook = usePaymentSessionTimeoutFromAPI({
        paymentSessionId,
        enabled: true, // Enable to fetch actual payment session expiration
        onTimeout: handleTimeout,
        onWarning: handleWarning,
        onActivity: handleActivity,
    })

    // Track the current timeout value (can be changed dynamically)
    // Initialize with API-based timeout if available, otherwise use config timeout
    const [currentTimeout, setCurrentTimeout] = useState(() => {
        // Will be updated once API data is loaded
        return timeoutConfig?.timeout || 60 * 60 * 1000
    })

    // Force timeout hook to re-initialize when timeout changes
    const [timeoutKey, setTimeoutKey] = useState(0)

    // Track if we've already initialized from API to prevent overwriting dynamic changes
    const [initializedFromAPI, setInitializedFromAPI] = useState(false)

    // Update currentTimeout when API data is loaded (only on initial load)
    useEffect(() => {
        if (initializedFromAPI) {
            // Already initialized, don't overwrite dynamic timeout changes
            return
        }

        if (!apiTimeoutHook.isLoading && apiTimeoutHook.remainingTime > 0 && !apiTimeoutHook.error) {
            // Use the remaining time from the payment session API
            setCurrentTimeout(apiTimeoutHook.remainingTime)
            setInitializedFromAPI(true)
            // eslint-disable-next-line no-console
            console.log('[SdkPageWithTimeout] Using payment session timeout from API:', apiTimeoutHook.remainingTime)
        } else if (apiTimeoutHook.error && !initializedFromAPI && timeoutConfig) {
            // Fallback to config timeout if API fails
            const configTimeout = timeoutConfig.timeout || 60 * 60 * 1000
            setCurrentTimeout(configTimeout)
            setInitializedFromAPI(true)
            console.warn('[SdkPageWithTimeout] API failed, using config timeout:', configTimeout)
        }
    }, [apiTimeoutHook.isLoading, apiTimeoutHook.remainingTime, apiTimeoutHook.error, timeoutConfig, initializedFromAPI])

    // Dynamic timeout hook for handling payment method changes
    const dynamicTimeout = useDynamicSessionTimeout({
        initialTimeout: currentTimeout,
        onTimeoutChange: ({ newTimeout, previousTimeout, reason }) => {
            logTimeoutEvent('timeout_changed', {
                newTimeout,
                previousTimeout,
                reason,
                timestamp: Date.now(),
            })
            // Update the current timeout state to trigger re-render of timeout hooks
            setCurrentTimeout(newTimeout)
            // Force timeout hook to re-initialize by changing the key
            setTimeoutKey(prev => prev + 1)
        },
    })

    // Listen for payment submission events from the modal (when Pay button is clicked)
    useEffect(() => {
        const handlePaymentSubmitted = event => {
            const { paymentMethod } = event.detail || {}

            if (paymentMethod && String(paymentMethod).toLowerCase().includes('virtual')) {
                setHideCountdownBar(true)
            }

            if (!dynamicTimeoutEnabled || !timeoutConfig?.dynamicTimeout) {
                return
            }

            if (!paymentMethod) {
                return
            }

            // Get timeout configuration for the selected payment method
            const methodTimeouts = timeoutConfig.dynamicTimeout.paymentMethodTimeouts || {}
            const newTimeout = methodTimeouts[String(paymentMethod)]

            if (newTimeout && newTimeout !== dynamicTimeout.getCurrentTimeout()) {
                // Change the timeout based on payment method when Pay button is clicked
                dynamicTimeout.changeTimeout(newTimeout, `Payment submitted with ${paymentMethod}`)
            }
        }

        // Listen for custom event from modal when Pay button is clicked
        document.addEventListener('paymentSubmitted', handlePaymentSubmitted)

        return () => {
            document.removeEventListener('paymentSubmitted', handlePaymentSubmitted)
        }
    }, [dynamicTimeoutEnabled, timeoutConfig, dynamicTimeout])

    // Listen for charge creation events
    useEffect(() => {
        const handleChargeCreated = event => {
            const { chargeId, paymentMethod } = event.detail || {}

            if (paymentMethod && String(paymentMethod).toLowerCase().includes('virtual')) {
                setHideCountdownBar(true)
            }

            if (!chargeId) {
                // eslint-disable-next-line no-console
                console.log('[SdkPageWithTimeout] No chargeId in chargeCreated event')
                return
            }

            // eslint-disable-next-line no-console
            console.log('[SdkPageWithTimeout] Charge created:', chargeId)
            // eslint-disable-next-line no-console
            console.log('[SdkPageWithTimeout] Payment method from event:', paymentMethod)
            // eslint-disable-next-line no-console
            console.log('[SdkPageWithTimeout] Full event detail:', event.detail)

            // Determine the timeout to use
            let newTimeout = null
            let reason = `Charge created: ${chargeId}`

            // Default charge timeout fallback (15 minutes) - used when payment method is undefined
            const DEFAULT_CHARGE_TIMEOUT = 15 * 60 * 1000

            if (!dynamicTimeoutEnabled || !timeoutConfig?.dynamicTimeout) {
                // Use default charge timeout - check if it exists in config first
                newTimeout =
                    timeoutConfig?.sessionTimeout?.dynamicTimeout?.chargeTimeout ||
                    timeoutConfig?.dynamicTimeout?.chargeTimeout ||
                    DEFAULT_CHARGE_TIMEOUT
                // eslint-disable-next-line no-console
                console.log('[SdkPageWithTimeout] Dynamic timeout not enabled, using default charge timeout:', newTimeout)
            } else {
                // Check if payment method has a specific timeout
                const methodTimeouts = timeoutConfig.dynamicTimeout.paymentMethodTimeouts || {}

                // eslint-disable-next-line no-console
                console.log('[SdkPageWithTimeout] Available method timeouts:', methodTimeouts)
                // eslint-disable-next-line no-console
                console.log('[SdkPageWithTimeout] Looking for timeout for method:', paymentMethod)

                if (paymentMethod && methodTimeouts[String(paymentMethod)]) {
                    // Use payment method-specific timeout
                    newTimeout = methodTimeouts[String(paymentMethod)]
                    reason = `Charge created with ${paymentMethod}: ${chargeId}`
                    // eslint-disable-next-line no-console
                    console.log(`[SdkPageWithTimeout] Found ${paymentMethod} timeout:`, newTimeout)
                } else if (!paymentMethod) {
                    // When payment method is undefined, always use the hardcoded 15-minute default
                    newTimeout = DEFAULT_CHARGE_TIMEOUT
                    // eslint-disable-next-line no-console
                    console.log(
                        '[SdkPageWithTimeout] Payment method is undefined, using hardcoded 15-minute default:',
                        newTimeout,
                    )
                } else {
                    // Payment method is defined but not in config, use configured chargeTimeout
                    newTimeout = timeoutConfig.dynamicTimeout.chargeTimeout || DEFAULT_CHARGE_TIMEOUT
                    // eslint-disable-next-line no-console
                    console.log('[SdkPageWithTimeout] Payment method not found in config, using chargeTimeout:', newTimeout)
                }
            }

            // Apply the timeout change
            if (dynamicTimeout && typeof dynamicTimeout.changeTimeout === 'function') {
                const currentTimeout = dynamicTimeout.getCurrentTimeout()

                if (newTimeout !== currentTimeout) {
                    dynamicTimeout.changeTimeout(newTimeout, reason)
                } else {
                    // eslint-disable-next-line no-console
                    console.log('[SdkPageWithTimeout] Timeout already set to target value')
                }
            }
        }

        // Listen for custom event when charge is created
        document.addEventListener('chargeCreated', handleChargeCreated)

        return () => {
            document.removeEventListener('chargeCreated', handleChargeCreated)
        }
    }, [dynamicTimeoutEnabled, timeoutConfig, dynamicTimeout])

    // Use config-based timeout which supports dynamic timeout changes
    // The timeoutKey forces the hook to re-initialize when timeout changes
    const configTimeoutHook = useIdleTimeout(
        timeoutConfig && timeoutConfig.enabled
            ? {
                  timeout: currentTimeout,
                  warningTime: timeoutConfig.warningTime,
                  onTimeout: handleTimeout,
                  onWarning: handleWarning,
                  onActivity: handleActivity,
                  events: [], // Disable activity tracking - timer should just count down
                  enabled: true,
                  key: timeoutKey,
              }
            : {
                  enabled: false,
              },
    )

    // Always use config-based hook for SDK page to support dynamic timeouts
    const activeHook = configTimeoutHook

    const {
        remainingTime,
        sessionDuration,
        isSessionExpired,
        isInactivityWarning,
        extendSession,
        triggerTimeout,
        triggerInactivityWarning,
        // getRemainingTimeFormatted, // Commented out - no longer used since IdleTimeoutModal is hidden
        // Config timeout hook uses different property names
        isIdle: configIsIdle,
        isWarning: configIsWarning,
        triggerWarning: configTriggerWarning,
    } = activeHook

    // Normalize the different property names between hooks
    const isWarning = isInactivityWarning || configIsWarning
    const isIdle = isSessionExpired || configIsIdle
    const triggerWarning = triggerInactivityWarning || configTriggerWarning

    // When timeout changes, reset the timer
    // Note: We use extendSession from activeHook which is stable
    useEffect(() => {
        // Skip the initial render (when timeout is set to initial value)
        if (currentTimeout === (timeoutConfig?.timeout || 60 * 60 * 1000)) {
            return
        }

        // eslint-disable-next-line no-console
        console.log('currentTimeout changed to:', currentTimeout)
        // eslint-disable-next-line no-console
        console.log('Resetting timer with new timeout')

        if (extendSession) {
            // extendSession will reset the timer with the new timeout value
            extendSession()
        }
    }, [currentTimeout, extendSession, timeoutConfig])

    // Check if debug mode is enabled (development or query parameter)
    const isDebugModeEnabled =
        process.env.NODE_ENV === 'development' ||
        new URLSearchParams(window.location.search).has('debug') ||
        timeoutConfig?.debug === true

    // Track if debug panel was manually closed
    const [debugPanelManuallyClosed, setDebugPanelManuallyClosed] = useState(false)

    // Show debug panel if debug mode is enabled and panel hasn't been manually closed
    useEffect(() => {
        if (isDebugModeEnabled && !debugPanelManuallyClosed) {
            setShowDebugPanel(false)
        }
    }, [isDebugModeEnabled, debugPanelManuallyClosed])

    const handleCloseDebugPanel = () => {
        setShowDebugPanel(false)
        setDebugPanelManuallyClosed(true)
    }

    const handleToggleDebugPanel = () => {
        setShowDebugPanel(!showDebugPanel)
        // Reset the manually closed flag when toggling via cog
        if (!showDebugPanel) {
            setDebugPanelManuallyClosed(false)
        }
    }

    // Add/remove body class for countdown bar spacing
    useEffect(() => {
        if (timeoutConfig && timeoutConfig.enabled && !showTimeoutScreen && !hideCountdownBar) {
            document.body.classList.add('timeout-bar-visible')
        } else {
            document.body.classList.remove('timeout-bar-visible')
        }

        // Cleanup on unmount
        return () => {
            document.body.classList.remove('timeout-bar-visible')
        }
    }, [timeoutConfig, showTimeoutScreen, hideCountdownBar])

    // Don't render anything until config is loaded
    if (!isConfigLoaded) {
        return <div>Loading...</div>
    }

    // If timeout is disabled, just render the original component
    if (!timeoutConfig || !timeoutConfig.enabled) {
        return <HostedPage {...props} />
    }

    return (
        <>
            <TimeoutCountdownBar
                remainingTime={remainingTime}
                totalTime={sessionDuration || currentTimeout}
                isVisible={!showTimeoutScreen && remainingTime > 0 && !hideCountdownBar}
                isWarning={isWarning}
                onExtendSession={handleExtendSession}
                config={{
                    primaryColor: timeoutConfig.modal?.primaryColor,
                    normalColor: '#28a745',
                    warningColor: '#ffc107',
                    criticalColor: '#dc3545',
                    normalText:
                        paymentSessionId && !apiTimeoutHook.error
                            ? 'Time left to complete transaction'
                            : 'Payment session time remaining',
                    warningText: 'Payment session expiring soon!',
                    extendButtonText: timeoutConfig.modal?.extendButtonText || 'Extend Session',
                }}
            />
            <HostedPage {...props} />
            {/* IdleTimeoutModal hidden - warnings no longer display dialog */}
            {/* <IdleTimeoutModal
                isVisible={isWarning && !showTimeoutScreen}
                remainingTime={getRemainingTimeFormatted()}
                onExtendSession={handleExtendSession}
                onLogout={handleLogout}
                config={timeoutConfig.modal}
            /> */}
            <SessionTimeoutScreen
                isVisible={showTimeoutScreen}
                onClose={handleTimeoutScreenClose}
                config={{
                    ...timeoutConfig.modal,
                    title: timeoutConfig.timeoutScreen?.title || 'Session Expired',
                    message:
                        timeoutConfig.timeoutScreen?.message ||
                        'Your session has expired due to inactivity. This window will close automatically.',
                    countdownMessage: timeoutConfig.timeoutScreen?.countdownMessage || 'Closing in {countdown} seconds...',
                    autoCloseDelay: timeoutConfig.timeoutScreen?.autoCloseDelay || 5000,
                }}
            />
            <TimeoutDebugPanel
                isVisible={showDebugPanel}
                onTriggerWarning={triggerWarning}
                onTriggerTimeout={triggerTimeout}
                onExtendSession={handleExtendSession}
                onClose={handleCloseDebugPanel}
                remainingTime={remainingTime}
                isWarning={isWarning}
                isIdle={isIdle || showTimeoutScreen}
            />
            <DebugCogToggle
                isVisible={isDebugModeEnabled}
                onClick={handleToggleDebugPanel}
                isDebugPanelVisible={showDebugPanel}
            />
        </>
    )
}

export default SdkPageWithTimeout
